import { isManager } from "../roles";
import type { SessionUser } from "../types";
import { ArrowRight, ClipboardPlus, Coins, History, PlusCircle, Radio } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Link } from "wouter";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import Modal from "../components/Modal";
import StatusBadge from "../components/StatusBadge";
import type { DashboardData, Service } from "../types";
import { formatCredits, formatDate, sourceLabelKey } from "../utils";

export default function OperationsPage({
  data,
  services,
  user,
  onChanged,
}: {
  data: DashboardData;
  services: Service[];
  user: SessionUser;
  onChanged: () => void;
}) {
  const t = useT();
  // Credit creation is manager-only (2026-08-18). Hiding the button is courtesy;
  // POST /api/admin/credits is the actual control and 403s regardless.
  const canChangeCredits = isManager(user.role);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const pending = data.requests.filter((request) => request.status === "pending");
  const offline = data.requests.filter((request) => request.source.startsWith("offline:")).slice(0, 6);

  return (
    <div className="page-stack">
      <section className="operations-hero">
        <div>
          <span className="section-eyebrow">Prismic-only workspace</span>
          <h2>Keep offline work inside the same account record</h2>
          <p>Log completed requests received through WhatsApp, email, meetings, or calls. The client sees the deduction and delivery source in the same ledger.</p>
        </div>
        <div className="operations-balance"><span>Available now</span><strong>{formatCredits(data.balance.available)} cr</strong><small>{formatCredits(data.balance.reserved)} reserved</small></div>
      </section>

      <section className="operation-actions">
        <button className="operation-action" onClick={() => setDeliveryOpen(true)}>
          <div className="operation-icon red"><ClipboardPlus size={23} /></div>
          <div><span>Record completed work</span><strong>Log an offline deliverable</strong><p>Creates the delivery record and credit deduction together.</p></div>
          <ArrowRight size={20} />
        </button>
        {canChangeCredits ? (
          <button className="operation-action" onClick={() => setCreditsOpen(true)}>
            <div className="operation-icon green"><PlusCircle size={23} /></div>
            <div><span>Maintain credit balance</span><strong>Add or adjust credits</strong><p>Record purchased credits, refunds, or approved corrections.</p></div>
            <ArrowRight size={20} />
          </button>
        ) : (
          <div className="operation-action" aria-disabled="true" style={{ opacity: 0.55, cursor: "not-allowed" }}>
            <div className="operation-icon green"><PlusCircle size={23} /></div>
            <div><span>Maintain credit balance</span><strong>Manager approval required</strong><p>Only a Prismic manager can record purchased credits, refunds, or corrections.</p></div>
          </div>
        )}
      </section>

      <section className="operations-grid">
        <article className="panel">
          <div className="panel-header compact"><div><span className="section-eyebrow">Needs attention</span><h3>Pending portal requests</h3></div><Link href="/requests" className="text-link">Review all <ArrowRight size={16} /></Link></div>
          {pending.length ? <div className="compact-list">{pending.slice(0, 5).map((request) => <div className="compact-list-row" key={request.id}><div><strong>{request.title}</strong><span>{request.service_name} · {formatCredits(request.estimated_credits)} cr</span></div><StatusBadge status={request.status} /></div>)}</div> : <div className="empty-compact"><Radio size={23} /><div><strong>No pending approvals</strong><span>The portal queue is clear.</span></div></div>}
        </article>

        <article className="panel">
          <div className="panel-header compact"><div><span className="section-eyebrow">Offline record</span><h3>Recently logged deliverables</h3></div><History size={19} /></div>
          {offline.length ? <div className="compact-list">{offline.map((request) => <div className="compact-list-row" key={request.id}><div><strong>{request.title}</strong><span>{t(sourceLabelKey(request.source))} · {formatDate(request.completed_at)}</span></div><strong>{formatCredits(request.final_credits || 0)} cr</strong></div>)}</div> : <div className="empty-compact"><History size={23} /><div><strong>No offline deliverables yet</strong><span>Use “Record completed work” when one is delivered.</span></div></div>}
        </article>
      </section>

      <Modal open={deliveryOpen} title="Record completed work" onClose={() => setDeliveryOpen(false)} wide>
        <OfflineDeliveryForm services={services} onCreated={() => { setDeliveryOpen(false); onChanged(); }} />
      </Modal>
      <Modal open={creditsOpen && canChangeCredits} title="Add or adjust credits" onClose={() => setCreditsOpen(false)}>
        <CreditEntryForm month={data.month} onCreated={() => { setCreditsOpen(false); onChanged(); }} />
      </Modal>
    </div>
  );
}

function OfflineDeliveryForm({ services, onCreated }: { services: Service[]; onCreated: () => void }) {
  const [serviceId, setServiceId] = useState(services[0]?.id || "");
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [credits, setCredits] = useState(0);
  const [deliveredAt, setDeliveredAt] = useState(new Date().toISOString().slice(0, 10));
  const [source, setSource] = useState("whatsapp");
  const [channel, setChannel] = useState("");
  const [language, setLanguage] = useState("Arabic & English");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = useMemo(() => services.find((service) => service.id === serviceId), [serviceId, services]);
  const suggested = (selected?.credits || 0) * quantity;

  useEffect(() => setCredits(suggested), [suggested]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await dashboardApi.logDelivery({ serviceId, title, brief, quantity, credits, deliveredAt, source, channel, language, note });
      toast.success(`Completed work recorded for ${formatCredits(credits)} credits.`);
      onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not record the deliverable.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form-stack" onSubmit={submit}>
      <div className="form-grid">
        <div className="form-field"><label htmlFor="delivery-service">Delivered service</label><select id="delivery-service" value={serviceId} onChange={(event) => setServiceId(event.target.value)}>{services.map((service) => <option key={service.id} value={service.id}>{service.name} · {service.included ? "Included" : `${formatCredits(service.credits)} cr`}</option>)}</select></div>
        <div className="form-field"><label htmlFor="delivery-source">Request source</label><select id="delivery-source" value={source} onChange={(event) => setSource(event.target.value)}><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="meeting">Meeting</option><option value="phone">Phone</option><option value="workshop">Workshop</option><option value="other">Other</option></select></div>
      </div>
      <div className="form-field"><label htmlFor="delivery-title">Deliverable title</label><input id="delivery-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Town hall coverage" required /></div>
      <div className="form-field"><label htmlFor="delivery-brief">What was delivered</label><textarea id="delivery-brief" rows={4} value={brief} onChange={(event) => setBrief(event.target.value)} placeholder="Describe the approved output and delivered formats" required /></div>
      <div className="form-grid form-grid-three">
        <div className="form-field"><label htmlFor="delivery-quantity">Quantity</label><input id="delivery-quantity" type="number" min="1" max="50" step="1" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} /></div>
        <div className="form-field"><label htmlFor="delivery-credits">Credits deducted</label><input id="delivery-credits" type="number" min="0" step="0.1" value={credits} onChange={(event) => setCredits(Number(event.target.value))} /></div>
        <div className="form-field"><label htmlFor="delivery-date">Delivered date</label><input id="delivery-date" type="date" value={deliveredAt} onChange={(event) => setDeliveredAt(event.target.value)} /></div>
      </div>
      <div className="form-grid">
        <div className="form-field"><label htmlFor="delivery-channel">Platform / channel</label><input id="delivery-channel" value={channel} onChange={(event) => setChannel(event.target.value)} placeholder="Instagram, X, internal, etc." /></div>
        <div className="form-field"><label htmlFor="delivery-language">Language</label><select id="delivery-language" value={language} onChange={(event) => setLanguage(event.target.value)}><option>Arabic & English</option><option>Arabic</option><option>English</option></select></div>
      </div>
      <div className="form-field"><label htmlFor="delivery-note">Ledger note</label><input id="delivery-note" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional approval or delivery reference" /></div>
      <div className="rate-preview"><Coins size={19} /><div><span>Agreed rate-card value</span><strong>{formatCredits(suggested)} credits suggested{credits !== suggested ? ` · adjusted to ${formatCredits(credits)}` : ""}</strong></div></div>
      <div className="form-actions"><button className="button button-primary" disabled={saving}>{saving ? "Recording…" : "Record delivery and deduction"}</button></div>
    </form>
  );
}

function CreditEntryForm({ month, onCreated }: { month: string; onCreated: () => void }) {
  const [kind, setKind] = useState("topup");
  const [credits, setCredits] = useState(1);
  const [title, setTitle] = useState("Additional production credits");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await dashboardApi.addCredits({ kind, credits, title, note, month });
      toast.success(`${formatCredits(credits)} credits recorded.`);
      onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update credits.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form-stack" onSubmit={submit}>
      <div className="form-field"><label htmlFor="credit-kind">Entry type</label><select id="credit-kind" value={kind} onChange={(event) => setKind(event.target.value)}><option value="topup">Purchased / additional credits</option><option value="refund">Credit refund</option><option value="adjustment">Approved adjustment</option><option value="rollover">Manual rollover correction</option></select></div>
      <div className="form-field"><label htmlFor="credit-amount">Credits</label><input id="credit-amount" type="number" step="0.1" min={kind === "adjustment" ? undefined : 0.1} value={credits} onChange={(event) => setCredits(Number(event.target.value))} /></div>
      <div className="form-field"><label htmlFor="credit-title">Title</label><input id="credit-title" value={title} onChange={(event) => setTitle(event.target.value)} required /></div>
      <div className="form-field"><label htmlFor="credit-note">Approval / PO reference</label><textarea id="credit-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional reference or explanation" /></div>
      <div className="rate-preview"><Coins size={19} /><div><span>Deducts from the retainer</span><strong>{formatCredits(credits)} credits</strong></div></div>
      <div className="form-actions"><button className="button button-primary" disabled={saving}>{saving ? "Saving…" : "Record credit entry"}</button></div>
    </form>
  );
}
